import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, catchError, map, tap, mergeMap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TokenData } from 'src/app/model/api/token-data';
import { AuthService } from '../../services/auth.service';
import * as AuthActions from './auth.actions';
import * as UserActions from '../user/user.actions';
import { langFromLanguage } from 'src/app/model/lang';
//TODOREPLACE import { SnackbarService } from 'src/app/services/snackbar.service';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import {
	customerInvitation, shareDeviceInvitation, sharingOrTransferring, transferSiteInvitation
} from 'src/app/model/api/invitation-data';
import {
	LOCALSTORAGE_PERSONIFIED_USER, LOCALSTORAGE_RETURN_URL,
	LOCALSTORAGE_TECHNICIAN_ID, LOCALSTORAGE_TOKEN_DATA, LOCALSTORAGE_USER_DATA
} from 'src/app/conf/user';
import { Store } from '@ngrx/store';
import * as NotificationsActions from '../notifications/notifications.actions';
import { AppState } from '../app.reducer';
//TODOREPLACE import { DateAdapter } from '@angular/material/core';
import { getAnalytics, logEvent } from 'firebase/analytics';
import packageJson from 'package.json';
import { selectCurrentLang, selectUser } from '../user/user.selector';
import { WebsocketService } from 'src/app/services/websocket.service';

@Injectable()
export class AuthEffects {
	constructor(
		private actions$: Actions,
		private authService: AuthService,
		private router: Router,
		private translate: TranslateService,
		private store: Store<AppState>,
		private websocketservice: WebsocketService,
	) { }

	authCode$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.authCode),
		switchMap((authData) => this.authService.authCode(
			authData.username,
			authData.password
		).pipe(
			map((authCodeData) => AuthActions.authCodeSuccess({ authCodeData })),
			catchError(error => {
				console.error(error);
				return of(AuthActions.authCodeFailed({ errorMessage: this.translate.instant('AUTH_ERROR') }));
			})
		))
	));

	getToken$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.authCodeSuccess),
		switchMap((authData) => this.authService.getToken(authData.authCodeData.code).pipe(
			map((tokenData) => {
				localStorage.setItem(LOCALSTORAGE_TOKEN_DATA, JSON.stringify(tokenData));
				return AuthActions.getTokenSuccess({
					tokenData,
					userId: this.authService.getTokenUserId(tokenData.access_token),
					redirect: true
				});
			}),
			catchError(error => of(AuthActions.getTokenFailed({ errorMessage: error.message })))
		))
	));

	authFailed$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.getTokenFailed),
		tap(() => {
			this.router.navigate(['/login']);
		})
	), { dispatch: false });

	//----------------- NEW 2FA Auth

	authMfaCode$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.authMfaCode),
		switchMap((authData) => this.authService.authMfaCode(
			authData.username,
			authData.password,
			authData.mfaCode,
		).pipe(
			map((authCodeData) => AuthActions.authMfaCodeSuccess({ authCodeData })),
			catchError(error => {
				if (error !== undefined && error.error !== undefined && error.error.error !== undefined && error.error.error === 'invalid_mfa_code') {
					return of(AuthActions.authMfaCodeInvalidMfaCode());
				}
				return of(AuthActions.authMfaCodeFailed({ errorMessage: this.translate.instant('AUTH_ERROR') }));
			})
		))
	));

	getMfaToken$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.authMfaCodeSuccess),
		switchMap((authData) => this.authService.getMfaToken(authData.authCodeData.code).pipe(
			map((tokenData) => {
				localStorage.setItem(LOCALSTORAGE_TOKEN_DATA, JSON.stringify(tokenData));
				return AuthActions.getMfaTokenSuccess({
					tokenData,
					userId: this.authService.getTokenUserId(tokenData.access_token),
					redirect: true
				});
			}),
			catchError(error => of(AuthActions.getMfaTokenFailed({ errorMessage: error.message })))
		))
	));

	mfaAuthFailed$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.getMfaTokenFailed),
		tap(() => {
			this.router.navigate(['/login']);
		})
	), { dispatch: false });

	//----------------- NEW 2FA Auth


	//TODOREPLACE
	setLang$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.autoLogin),
		switchMap(() => {
			// const lang = localStorage.getItem(LOCALSTORAGE_LANGUAGE);
			// if (lang) {
			//     this.translate.use(lang);
			// }
			const lang = langFromLanguage();
			this.translate.use(lang.code);
			return [UserActions.setCurrentLang({ lang })];
		})
	));

	authLogout$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.authLogout),
		withLatestFrom(this.store.select(selectUser), this.store.select(selectCurrentLang)),
		tap(() => {
			localStorage.removeItem(LOCALSTORAGE_USER_DATA);
			localStorage.removeItem(LOCALSTORAGE_PERSONIFIED_USER);
			localStorage.removeItem(LOCALSTORAGE_TOKEN_DATA);
			this.websocketservice.muteWebSocket();
			if (!this.router.url.includes('intercomsetup')) {
				this.router.navigate(['/login']);
			}
		})
	), { dispatch: false });

	autoLogin$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.autoLogin),
		map((action) => {
			const lsTokenData = localStorage != null ? localStorage.getItem(LOCALSTORAGE_TOKEN_DATA) : null;
			if (!lsTokenData) {
				return AuthActions.unauthRoute({ dest: action.dest });
			}
			let tokenData: TokenData | null = null;
			tokenData = JSON.parse(lsTokenData);
			if (tokenData?.access_token) {
				let redirect = false;
				if (action.dest === '/') {
					redirect = true;
				}
				return AuthActions.getTokenSuccess({
					tokenData,
					userId: this.authService.getTokenUserId(tokenData.access_token),
					redirect
				});
			}
			return AuthActions.unauthRoute({ dest: action.dest });
		})
	));

	signup$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.signup),
		switchMap(action => {
			if (action.isTechnician) {
				return this.authService.signUpTechnician(action.user)
					.pipe(
						map(resp => {
							if (resp.Success) {
								const nation = action.user ? action.user.CountryName.toUpperCase() : 'no_data';
								logEvent(getAnalytics(), 'signup_completed', { user_role: 'TECHNICIAN', lang: action.lang.code, nation: nation, app_version: packageJson.version });
								return AuthActions.technicianSignupSuccess({
									message: this.translate.instant('USER_SIGNUP_SUCCESS'),
									userId: resp.Data.Id,
									fromInvitation: false
								});
							} else {
								return AuthActions.signupFailed({ errorMessage: resp.ErrorMessage });
							}
						}),
						catchError(error => of(AuthActions.signupFailed({ errorMessage: error.message })))
					);
			} else {
				return this.authService.signUpUser(action.user).pipe(
					map(resp => {
						if (resp.Success) {
							const pars = {
								message: this.translate.instant('USER_SIGNUP_SUCCESS'),
								userId: resp.Data.Id,
								fromInvitation: action.fromInvitation,
								invitationData: action.invitationData
							};
							const nation = action.user ? action.user.CountryName.toUpperCase() : 'no_data';
							logEvent(getAnalytics(), 'signup_completed', { user_role: 'USER', lang: action.lang.code, nation: nation, app_version: packageJson.version });
							return AuthActions.userSignupSuccess(pars);
						} else {
							return AuthActions.signupFailed({ errorMessage: resp.ErrorMessage });
						}
					}),
					catchError(error => of(AuthActions.signupFailed({ errorMessage: error.message })))
				);
			}
		})
	));

	userSignupSuccessSaveAndBackToLogin$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.userSignupSuccess),
		tap(action => {
			if (action.fromInvitation && customerInvitation(action.invitationData)) {
				const returnUrl = { path: '/home/support' };//TODO RIMETTERE sites
				localStorage.setItem(LOCALSTORAGE_RETURN_URL, JSON.stringify(returnUrl));
				localStorage.setItem(LOCALSTORAGE_TECHNICIAN_ID, action.invitationData.TechnicianId.toString());
			}
			this.router.navigate(['/login']);
		})
	), { dispatch: false });

	techSignupSuccessSaveAndBackToLogin$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.technicianSignupSuccess),
		tap(action => {
			if (action.fromInvitation && customerInvitation(action.invitationData)) {
				const returnUrl = { path: '/technician/customers' };//TODO RIMETTERE support
				localStorage.setItem(LOCALSTORAGE_RETURN_URL, JSON.stringify(returnUrl));
				localStorage.setItem(LOCALSTORAGE_TECHNICIAN_ID, action.invitationData.TechnicianId.toString());
			}
			this.router.navigate(['/login']);
		})
	), { dispatch: false });

	signupAssignTechnician$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.userSignupSuccess),
		mergeMap(action => {
			if (action.invitationData && customerInvitation(action.invitationData)) {
				return [UserActions.assignTechnicianToUser({
					userId: action.userId,
					technicianId: action.invitationData.TechnicianId,
					verification: action.invitationData.Verification
				})];
			} else {
				return of({ type: 'NOT_EXISTS' });
			}
		})
	));

	signupConfirm$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.userSignupSuccess),
		mergeMap(action => {
			const { invitationData, fromInvitation } = action;
			if (!fromInvitation) {
				return [];
			}
			if (!sharingOrTransferring(invitationData)) {
				return [];
			}
			if (shareDeviceInvitation(invitationData)) {
				return [UserActions.confirmShareDevice({ invitationData: { ...invitationData, UserId: action.userId } })];
			}
			if (transferSiteInvitation(invitationData)) {
				const pars = {
					siteId: invitationData.SiteId,
					ownerId: invitationData.OwnerId,
					userId: action.userId,
					verification: invitationData.Verification
				};
				return [UserActions.confirmSiteTransfer(pars)];
			}
			return [];
		})
	));

	resetPassword$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.resetPassword),
		map(action => action),
		switchMap(payload => this.authService.resetPassword(payload.email)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return AuthActions.resetPasswordSuccess({ message: this.translate.instant('PASSWORD_RESET_SUCCESS') });
					} else {
						return AuthActions.resetPasswordFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					console.error(error);
					return of(AuthActions.resetPasswordFailed({ errorMessage: error.message }));
				})
			)
		)
	));

	resetPasswordSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.resetPasswordSuccess),
		withLatestFrom(this.store.select(selectCurrentLang)),
		//tap(() => this.dialog.closeAll()), //TODO REPLACE
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		tap(([action, currentLang]) => {
			const roleUser = 'not_authenticated';
			const langCode = currentLang ? currentLang.code : 'no_data';
			logEvent(getAnalytics(), 'forgotten_password', { user_role: roleUser, lang: langCode, app_version: packageJson.version });
		}),
	), { dispatch: false });

	updatePassword$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.updatePassword),
		switchMap(payload => this.authService.updatePassword(payload.email, payload.pwd, payload.verification, payload.time)
			.pipe(
				map(resp => {
					if (resp.Success) {
						// this.messages.success('PASSWORD_UPDATED');
						const nation = payload.countryName ? payload.countryName.toUpperCase() : 'no_data';
						logEvent(getAnalytics(), 'update_password', { lang: payload.lang.code, nation: nation, app_version: packageJson.version });
						this.router.navigate(['/login']);
						return AuthActions.updatePasswordSuccess({ message: this.translate.instant('PASSWORD_UPDATED') });
					} else {
						return AuthActions.updatePasswordFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => of(AuthActions.updatePasswordFailed({ errorMessage: error.message })))
			)
		)
	));

	confirmEmail$ = createEffect(() => this.actions$.pipe(
		ofType(AuthActions.confirmEmail),
		switchMap(payload => this.authService.confirmEmail(payload.id, payload.data).pipe(
			map(resp => {
				if (resp.Success) {
					return AuthActions.confirmEmailSuccess({ message: this.translate.instant('EMAIL_CONFIRMED') });
				} else {
					return AuthActions.confirmEmailFailed({ errorMessage: resp.ErrorMessage });
				}
			}),
			catchError(error => of(AuthActions.confirmEmailFailed({ errorMessage: error.message })))
		))
	));

	notifySuccess$ = createEffect(() => this.actions$.pipe(
		ofType(
			AuthActions.resetPasswordSuccess,
			AuthActions.resetPasswordSuccess,
			AuthActions.confirmEmailSuccess,
			AuthActions.updatePasswordSuccess
		),
		tap((action) => {
			if (action.message === '') {
				return;
			}
			const notification = new Notification("AUTH", action.message, NotificationLevel.Success, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		})
	), { dispatch: false });

	notifyError$ = createEffect(() => this.actions$.pipe(
		ofType(
			AuthActions.signupFailed,
			AuthActions.resetPasswordFailed,
			AuthActions.updatePasswordFailed,
			AuthActions.authCodeFailed,
			AuthActions.confirmEmailFailed,
			AuthActions.authMfaCodeFailed,
		),
		tap(() => {
			const notification = new Notification("AUTENTICATION", "AUTH_ERROR", NotificationLevel.Error, "");
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		})
	), { dispatch: false });

}
